import { T } from "../lib/fc3i18n";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Lock, Clock, Flame, CalendarHeart, Sparkles, Star } from "lucide-react";
import { CATEGORIES, PHASES, WORKOUTS, SAFETY, phaseForDay } from "./data";
import WorkoutPlayer, { completedThisWeek } from "./WorkoutPlayer";
import GymFinder from "./GymFinder";
const SANS = {
  fontFamily: "var(--fc-sans)",
  fontWeight: 800
};
const catOf = k => CATEGORIES.find(c => c.key === k) || CATEGORIES[0];
const DAY = 86400000;
function PhasePicker({
  phase,
  setPhase
}) {
  const [last, setLast] = useState("");
  const [len, setLen] = useState(28);
  const calc = e => {
    e.preventDefault();
    if (!last) return;
    const [y, m, d] = last.split("-").map(Number);
    const start = new Date(y, m - 1, d);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diff = Math.floor((today - start) / DAY);
    if (diff < 0) return;
    const day = diff % len + 1;
    setPhase(phaseForDay(day, len), day);
  };
  return <div className="grid lg:grid-cols-12 gap-5">
      <div className="lg:col-span-7 grid grid-cols-2 gap-3">
        {Object.entries(PHASES).map(([k, p]) => <button key={k} type="button" onClick={() => setPhase(k)} aria-pressed={phase === k} className={`text-left rounded-[22px] p-4 md:p-5 border-2 transition-all ${phase === k ? "shadow-[0_20px_40px_-26px_rgba(43,33,64,0.5)] -translate-y-0.5" : "border-transparent"}`} style={{
        background: p.bg,
        borderColor: phase === k ? p.color : "transparent"
      }}>
            <span className="block w-3 h-3 rounded-full" style={{
          background: p.color
        }} />
            <span className="mt-3 block text-[16px] font-extrabold text-[#1E1A2B]">{T(p.label)}</span>
            <span className="block text-[13px] font-semibold text-[#5F5A70]">{T(p.days)} {T("(28-day cycle)")}</span>
          </button>)}
      </div>
      <form onSubmit={calc} className="lg:col-span-5 rounded-[22px] bg-white border border-[#F1E6EA] p-5 flex flex-col gap-3">
        <p className="text-[15px] font-extrabold text-[#1E1A2B] flex items-center gap-2"><CalendarHeart className="w-5 h-5 text-[#E0306F]" /> {T("Not sure? Work it out")}</p>
        <label className="text-[14px] font-semibold text-[#2B2140]" htmlFor="fit-last">{T("First day of your last period")}</label>
        <input id="fit-last" type="date" value={last} onChange={e => setLast(e.target.value)} max={new Date().toISOString().slice(0, 10)} className="min-h-[48px] rounded-xl border border-[#E9DCE3] px-3 text-[16px]" required />
        <label className="text-[14px] font-semibold text-[#2B2140]" htmlFor="fit-len">{T("Usual cycle length (days)")}</label>
        <input id="fit-len" type="number" min={21} max={45} value={len} onChange={e => setLen(Math.min(45, Math.max(21, Number(e.target.value) || 28)))} className="min-h-[48px] rounded-xl border border-[#E9DCE3] px-3 text-[16px] w-28" />
        <button type="submit" className="fc-btn-coral min-h-[48px] rounded-full font-extrabold">{T("Show my phase")}</button>
        <p className="text-[12px] text-[#7A7488]">{T("An estimate for workout ideas only. Calculated in your browser.")}</p>
      </form>
    </div>;
}
function WorkoutCard({
  w,
  locked,
  onOpen
}) {
  const c = catOf(w.category);
  return <button type="button" onClick={() => onOpen(w)} data-testid={`workout-${w.id}`} className="group text-left h-full rounded-[26px] bg-white border border-[#F1E6EA] p-5 flex flex-col hover:-translate-y-1 hover:shadow-[0_30px_60px_-34px_rgba(43,33,64,0.45)] transition-all">
      <div className="flex items-center justify-between gap-2">
        <span className="rounded-full px-3 py-1 text-[12px] font-extrabold" style={{
        background: c.bg,
        color: c.color
      }}>{T(c.label)}</span>
        {locked ? <span className="inline-flex items-center gap-1 rounded-full bg-[#2B2140] text-white px-2.5 py-1 text-[11px] font-extrabold"><Lock className="w-3 h-3" /> {T("Premium")}</span> : w.free ? <span className="rounded-full bg-[#E3F8F2] text-[#13896F] px-2.5 py-1 text-[11px] font-extrabold">{T("Free")}</span> : <span className="inline-flex items-center gap-1 rounded-full bg-[#EFEBFF] text-[#5A49E0] px-2.5 py-1 text-[11px] font-extrabold"><Star className="w-3 h-3" fill="currentColor" /> {T("Premium")}</span>}
      </div>
      <h3 className="mt-4 text-[19px] leading-snug text-[#1E1A2B]" style={SANS}>{T(w.title)}</h3>
      <p className="mt-1.5 text-[14px] text-[#5F5A70] line-clamp-2">{T(w.blurb)}</p>
      <div className="mt-auto pt-4 flex items-center justify-between">
        <span className="flex items-center gap-3 text-[13px] font-bold text-[#5F5A70]">
          <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{w.minutes} {T("min")}</span>
          <span className="flex items-center gap-1"><Flame className="w-4 h-4" />{T(w.level)}</span>
        </span>
        <span className="w-11 h-11 rounded-full flex items-center justify-center text-white transition-transform group-hover:scale-110" style={{
        background: locked ? "#9A94A8" : c.color
      }}>
          {locked ? <Lock className="w-4 h-4" /> : <Play className="w-4 h-4" fill="currentColor" />}
        </span>
      </div>
    </button>;
}
export default function FitnessHub({
  unlocked = false,
  lockedHref = "/app/fitness",
  showGyms = true
}) {
  const [cat, setCat] = useState("all");
  const [phase, setPhaseState] = useState("follicular");
  const [phaseDay, setPhaseDay] = useState(null);
  const [open, setOpen] = useState(null);
  const [lockFor, setLockFor] = useState(null);
  const [week, setWeek] = useState(0);
  useEffect(() => {
    setWeek(completedThisWeek());
  }, [open]);
  const setPhase = (k, day = null) => {
    setPhaseState(k);
    setPhaseDay(day);
  };
  const isLocked = w => !w.free && !unlocked;
  const list = useMemo(() => WORKOUTS.filter(w => cat === "all" || w.category === cat), [cat]);
  const forPhase = useMemo(() => WORKOUTS.filter(w => w.phases.includes(phase)).slice(0, 4), [phase]);
  const openW = w => isLocked(w) ? setLockFor(w) : setOpen(w);
  const P = PHASES[phase];
  return <div className="space-y-20 lg:space-y-28">
      {/* Phase-synced */}
      <section data-testid="fitness-phase">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div className="max-w-2xl">
            <p className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-[13px] font-bold mb-4 bg-[#FFE9EF] text-[#D02868]"><Sparkles className="w-4 h-4" /> {T("Only at FemCare")}</p>
            <h2 className="text-[32px] sm:text-[40px] lg:text-[48px] leading-[1.06] tracking-[-0.025em] text-[#1E1A2B]" style={SANS}>{T("Workouts that match your cycle")}</h2>
            <p className="mt-3 text-[17px] text-[#5F5A70]">{T("Pick your phase and we'll suggest workouts that many women find feel right at that time. Always listen to your body.")}</p>
          </div>
          <p className="text-[14px] font-bold text-[#5F5A70] bg-white rounded-full px-4 py-2 border border-[#F1E6EA] self-start md:self-auto">{T("This week: {{n}} workout(s) on this device", { n: week })}</p>
        </div>
        <PhasePicker phase={phase} setPhase={setPhase} />
        <div className="mt-6 rounded-[26px] p-5 md:p-7" style={{
        background: P.bg
      }}>
          <p className="text-[15px] font-extrabold" style={{
          color: P.color
        }}>
            {T(P.label)}{phaseDay ? ` · ${T("estimated cycle day {{n}}", { n: phaseDay })}` : ""}
          </p>
          <p className="mt-1 text-[16px] text-[#2B2140] max-w-3xl">{T(P.tip)}</p>
          <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {forPhase.map(w => <WorkoutCard key={w.id} w={w} locked={isLocked(w)} onOpen={openW} />)}
          </div>
        </div>
      </section>

      {/* Library */}
      <section data-testid="fitness-library">
        <h2 className="text-[32px] sm:text-[40px] lg:text-[48px] leading-[1.06] tracking-[-0.025em] text-[#1E1A2B]" style={SANS}>{T("Workout library")}</h2>
        <p className="mt-3 text-[17px] text-[#5F5A70] max-w-2xl">{T("Yoga, dance cardio (Zumba-style), strength, Pilates and walking plans — follow along with a timer and voice guidance. No equipment needed for most.")}</p>
        <div className="mt-7 flex gap-2 overflow-x-auto pb-2 -mx-1 px-1" role="tablist" aria-label={T("Workout categories")}>
          {CATEGORIES.map(c => <button key={c.key} type="button" role="tab" aria-selected={cat === c.key} onClick={() => setCat(c.key)} className="shrink-0 min-h-[44px] rounded-full px-5 text-[14px] font-extrabold border transition-colors" style={cat === c.key ? {
          background: c.color,
          color: "#fff",
          borderColor: c.color
        } : {
          background: "#fff",
          color: "#2B2140",
          borderColor: "#E9DCE3"
        }}>
              {T(c.label)}
            </button>)}
        </div>
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {list.map(w => <WorkoutCard key={w.id} w={w} locked={isLocked(w)} onOpen={openW} />)}
        </div>
        <p className="mt-6 text-[13px] leading-relaxed text-[#7A7488] max-w-4xl">{T(SAFETY)}</p>
      </section>

      {showGyms && <section id="gyms" className="scroll-mt-24" data-testid="fitness-gyms">
          <h2 className="text-[32px] sm:text-[40px] lg:text-[48px] leading-[1.06] tracking-[-0.025em] text-[#1E1A2B]" style={SANS}>{T("Gyms & studios near you")}</h2>
          <p className="mt-3 mb-7 text-[17px] text-[#5F5A70] max-w-2xl">{T("Find gyms, yoga and dance studios around you, with women-only places highlighted where they're listed.")}</p>
          <GymFinder />
        </section>}

      {open && <WorkoutPlayer workout={open} accent={catOf(open.category).color} onClose={() => setOpen(null)} />}

      {lockFor && <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={T("Premium workout")}>
          <div className="absolute inset-0 bg-[#1E1A2B]/60 backdrop-blur-sm" onClick={() => setLockFor(null)} aria-hidden="true" />
          <div className="relative w-full max-w-md rounded-[30px] bg-white p-7 text-center shadow-2xl">
            <div className="mx-auto w-16 h-16 rounded-2xl bg-[#EFEBFF] text-[#5A49E0] flex items-center justify-center"><Lock className="w-8 h-8" /></div>
            <h3 className="mt-4 text-[24px] text-[#1E1A2B]" style={SANS}>{T(lockFor.title)}</h3>
            <p className="mt-2 text-[15px] text-[#5F5A70]">{T("This workout is part of FemCare Premium, along with the full Fitness library, AI insights and Pregnancy Mode.")}</p>
            <div className="mt-6 flex flex-col gap-2">
              <Link to={lockedHref} className="fc-btn-violet inline-flex items-center justify-center min-h-[52px] rounded-full font-extrabold">{T("I have Premium — log in")}</Link>
              <Link to="/pricing" className="fc-btn-coral inline-flex items-center justify-center min-h-[52px] rounded-full font-extrabold">{T("See Premium plans")}</Link>
              <button type="button" onClick={() => setLockFor(null)} className="min-h-[48px] rounded-full font-bold text-[#5F5A70]">{T("Not now")}</button>
            </div>
          </div>
        </div>}
    </div>;
}